import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { MyButton, MyTextField, myToaster } from '@interstellar-component'
import { InfoCircle, Key01, Passcode, Save01, User01 } from '@untitled-ui/icons-react'
import { encryptPassword } from '@src/services/Helper'

import { CompanyService } from '../service'
import { AGREEMENT_STALE_CODES, credentialGate, withAgreementMessage } from './agreementStatus'

/**
 * Why the CLIK credential form is locked (see agreementStatus.credentialGate):
 * the membership agreement is still waiting for the CLIK review or the CLIK
 * countersignature, or it was declined / voided. Shared by this form and the
 * detail slider's Result tab.
 */
export function CredentialGateNote({ gate }) {
  if (!gate?.blocked) return null
  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-lg border border-warning/200 bg-warning/50 px-3 py-2.5"
    >
      <InfoCircle className="mt-0.5 size-4 shrink-0 text-warning/600" stroke="currentColor" aria-hidden />
      <p className="text-xs-regular leading-relaxed text-warning/700">{gate.note}</p>
    </div>
  )
}

CredentialGateNote.propTypes = {
  gate: PropTypes.shape({ blocked: PropTypes.bool, note: PropTypes.string }),
}

CredentialGateNote.defaultProps = { gate: null }

/**
 * Toast a failed credential validate. The agreement 409s
 * (awaiting_clik_signature / agreement_not_executed) get a plain sentence; they
 * also mean the page's agreement state is stale, so `onStale` re-reads it.
 */
export function handleCredentialError(err, onStale) {
  myToaster(withAgreementMessage(err))
  if (AGREEMENT_STALE_CODES.includes(err?.code)) onStale?.()
}

/**
 * CLIK API credential form (username / password / api key + Reset / Validate).
 * Same activation form as the detail slider's Result tab, extracted so the
 * Company profile page can reuse it. Password is re-entered each time (the stored
 * value is encrypted); Validate probes CLIK and activates the company on success.
 */
export default function CompanyCredentialForm({
  companyId,
  company,
  generalInformation,
  onValidated,
  onAgreementChanged,
}) {
  const [form, setForm] = useState({ username: '', password: '', api_key: '' })
  const [submitting, setSubmitting] = useState(false)
  // Locked until CLIK has countersigned the membership agreement.
  const gate = credentialGate(company)
  const locked = submitting || gate.blocked

  useEffect(() => {
    setForm((prev) => ({
      username: generalInformation?.clik_username ?? '',
      // API key is a secret — leave it blank like the password instead of
      // pre-filling the stored value. A blank submit sends no api_key, and the
      // BE upsert ignores an undefined field, so the stored key is preserved.
      api_key: prev.api_key,
      password: prev.password,
    }))
  }, [generalInformation])

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))

  const handleReset = () => {
    setForm({
      username: generalInformation?.clik_username ?? '',
      api_key: '',
      password: '',
    })
  }

  const handleValidate = async () => {
    if (!companyId || submitting || gate.blocked) return
    if (!form.username?.trim() || !form.password?.trim()) {
      myToaster({ message: 'Username and password are required' })
      return
    }
    setSubmitting(true)
    try {
      const res = await CompanyService.validateCredential(companyId, {
        username: form.username.trim(),
        password: encryptPassword(form.password),
        api_key: form.api_key?.trim() || undefined,
      })
      myToaster(res)
      setForm((prev) => ({ ...prev, password: '' }))
      onValidated?.()
    } catch (err) {
      handleCredentialError(err, onAgreementChanged)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
        <h2 className="text-sm-semibold text-gray-900">API Credentials</h2>
      </div>
      <div className="flex flex-col gap-4 px-5 py-4">
        <p className="text-xs-regular leading-relaxed text-gray-600">
          Used to authenticate and authorize API requests from system.
        </p>
        <CredentialGateNote gate={gate} />
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm-medium text-gray-700" htmlFor="profile-username">
              Username
            </label>
            <MyTextField
              id="profile-username"
              disabled={gate.blocked}
              name="username"
              value={form.username}
              onChangeForm={setField('username')}
              placeholder="e.g. 3h3nnrf8inni871bb31884h12"
              margin="7px 12px"
              startAdornment={<User01 className="size-[18px] text-gray-400" stroke="currentColor" />}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm-medium text-gray-700" htmlFor="profile-password">
              Password
            </label>
            <MyTextField
              id="profile-password"
              disabled={gate.blocked}
              name="password"
              type="password"
              value={form.password}
              onChangeForm={setField('password')}
              placeholder="Enter the CLIK password"
              margin="7px 12px"
              startAdornment={<Passcode className="size-[18px] text-gray-400" stroke="currentColor" />}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm-medium text-gray-700" htmlFor="profile-api-key">
              API key
            </label>
            <MyTextField
              id="profile-api-key"
              disabled={gate.blocked}
              name="api_key"
              value={form.api_key}
              onChangeForm={setField('api_key')}
              placeholder="e.g. 3h3nnrf8inni871bb31884h12"
              margin="7px 12px"
              startAdornment={<Key01 className="size-[18px] text-gray-400" stroke="currentColor" />}
            />
          </div>
        </div>
        <hr className="border-gray-200" />
        <div className="flex justify-end gap-3 pt-1">
          <MyButton
            color="secondary"
            variant="outlined"
            size="sm"
            type="button"
            disabled={locked}
            onClick={handleReset}
          >
            <p className="text-sm-semibold">Reset</p>
          </MyButton>
          <MyButton
            color="primary"
            variant="outlined"
            size="sm"
            type="button"
            disabled={locked}
            onClick={handleValidate}
          >
            <Save01 className="size-4" stroke="currentColor" aria-hidden />
            <p className="text-sm-semibold">{submitting ? 'Validating…' : 'Validate'}</p>
          </MyButton>
        </div>
      </div>
    </div>
  )
}

CompanyCredentialForm.propTypes = {
  companyId: PropTypes.string,
  // Company payload carrying agreement_status / agreement (drives the lock).
  company: PropTypes.object,
  generalInformation: PropTypes.object,
  onValidated: PropTypes.func,
  // Re-read the company after an agreement 409 (the lock was stale).
  onAgreementChanged: PropTypes.func,
}

CompanyCredentialForm.defaultProps = {
  companyId: null,
  company: null,
  generalInformation: null,
  onValidated: undefined,
  onAgreementChanged: undefined,
}
